// App.tsx
import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { Header } from './components/Header';
import { LandingPage } from './components/LandingPage';
import { CreateDecisionPage } from './components/CreateDecisionPage';
import { DecisionPage } from './components/DecisionPage';
import { ResponseView } from './components/ResponseView';
import { ShareDecisionPage } from './components/ShareDecisionPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import { Footer } from './components/ui/footer';
import { User } from 'firebase/auth';
import Analytics, { identifyUser } from './services/analytics';
import { MetaTags } from './components/MetaTags';
import { HelmetProvider } from 'react-helmet-async';
import { doc, getDoc } from 'firebase/firestore';
import type { Decision } from './types/decision';
import { EditDecisionPage } from './components/EditDecisionPage';


// Create a context for sharing decision data
export const DecisionContext = createContext<{
  currentDecision: Decision | null;
  setCurrentDecision: React.Dispatch<React.SetStateAction<Decision | null>>;
}>({
  currentDecision: null,
  setCurrentDecision: () => {},
});

// Helper hook to use the decision context
export const useDecision = () => useContext(DecisionContext);

// Add ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [_error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      identifyUser(user);
    });
    return () => unsubscribe();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AppContent
          user={user}
          setError={setError}
        />
      </Router>
    </HelmetProvider>
  );
}

// Component for the main content with routing
function AppContent({ 
  user,  
  setError 
}: { 
  user: User | null, 
  setError: (error: string | null) => void
}) {
  const location = useLocation();
  const [currentDecision, setCurrentDecision] = useState<Decision | null>(null);
  
  // Determine page type for analytics and meta tags
  const pageType = useMemo(() => {
    const path = location.pathname;
    const pathParts = path.split('/').filter(Boolean);
    
    if (path === '/') {
      return 'landing';
    } else if (path === '/create') {
      return 'create_decision';
    } else if (pathParts[0] === 'd' && pathParts.length > 1) {
      const _decisionId = pathParts[1];
      if (pathParts.length === 2) {
        return 'decision';
      } else if (pathParts[2] === 'chat') {
        return 'chat';
      } else if (pathParts[2] === 'share') {
        return 'share';
      }
    } else if (path === '/terms') {
      return 'terms';
    } else if (path === '/privacy') {
      return 'privacy';
    }
    
    return 'landing';
  }, [location.pathname]);
  
  // Load decision data when route changes to a decision page
  useEffect(() => {
    const fetchDecision = async () => {
      const pathParts = location.pathname.split('/').filter(Boolean);
      
      // Only fetch if we're on a decision-related page
      if (pathParts[0] === 'd' && pathParts.length > 1) {
        const decisionId = pathParts[1];
        
        try {
          const decisionRef = doc(db, 'decisions', decisionId);
          const decisionSnap = await getDoc(decisionRef);
          
          if (decisionSnap.exists()) {
            setCurrentDecision({
              id: decisionSnap.id,
              ...decisionSnap.data()
            } as Decision);
          } else {
            setCurrentDecision(null);
          }
        } catch (err) {
          console.error('Error fetching decision for meta tags:', err);
          setCurrentDecision(null);
        }
      } else {
        // Reset decision data when not on a decision page
        setCurrentDecision(null);
      }
    };
    
    fetchDecision();
  }, [location.pathname]);

  useEffect(() => {
    // Extract meaningful path information
    const path = location.pathname;
    const pathParts = path.split('/').filter(Boolean);
    
    // Determine page type for analytics
    let analyticsPageType = 'other';
    let decisionId = null;
    
    if (path === '/') {
      analyticsPageType = 'landing';
    } else if (path === '/create') {
      analyticsPageType = 'create_decision';
    } else if (pathParts[0] === 'd' && pathParts.length > 1) {
      decisionId = pathParts[1];
      if (pathParts.length === 2) {
        analyticsPageType = 'view_response';
      } else if (pathParts[2] === 'chat') {
        analyticsPageType = 'chat';
      } else if (pathParts[2] === 'share') {
        analyticsPageType = 'share';
      }
    } else if (path === '/terms') {
      analyticsPageType = 'terms';
    } else if (path === '/privacy') {
      analyticsPageType = 'privacy';
    }
    
    // Track page view
    Analytics.trackEvent('page_view', { 
      pageType: analyticsPageType, 
      path,
      decisionId // Include decisionId for all page types, will be null when not applicable
    });
    
  }, [location]);

  // Use useMemo to avoid recalculating on every render
  const isChatRoute = useMemo(() => {
    return location.pathname.startsWith('/d/') && 
           location.pathname.endsWith('/chat');
  }, [location.pathname]);
  
  return (
    <DecisionContext.Provider value={{ currentDecision, setCurrentDecision }}>
      <div className="flex flex-col min-h-screen">
        {/* Add ScrollToTop component here */}
        <ScrollToTop />
        {/* Add MetaTags component */}
        <MetaTags 
          decision={currentDecision} 
          pageType={pageType as any} 
        />
        <Header
          user={user}
          onError={setError}
          isFixed={isChatRoute}
        />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<LandingPage user={user} onError={setError} />} />
            <Route
              path="/create"
              element={user ? <CreateDecisionPage user={user} onError={setError} /> : <Navigate to="/" />}
            />
            <Route
              path="/d/:decisionId/chat"
              element={
                <div className={isChatRoute ? 'pt-16 md:pt-24' : ''}>
                  <DecisionPage />
                </div>
              }
            />
            <Route path="/d/:decisionId/share" element={<ShareDecisionPage />} />
            <Route
              path="/d/:decisionId"
              element={
                <div className={isChatRoute ? 'pt-16 md:pt-24' : ''}>
                  <ResponseView user={user} />
                </div>
              }
            />
            <Route
              path="/d/:decisionId/edit"
              element={user ? <EditDecisionPage user={user} onError={setError} /> : <Navigate to="/" />}
            />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
          </Routes>
        </main>
        {!isChatRoute && <Footer />}
      </div>
    </DecisionContext.Provider>
  );
}

export default App;